<template>
  <div class="wrapper">
    <Loader :is-visible="loading" />

    <div class="animated fadeIn">
      <DynamicContentFilters
        :write-permission="writePermission"
        @apply="applyFilters"
        @add="modal.show = true"
      />

      <div class="card">
        <div class="card-header d-flex align-items-center">
          <i class="fa fa-bullhorn"></i> Événements
          <span v-if="total" class="ml-05">({{ total }})</span>
        </div>

        <div class="card-block">
          <div v-if="specialEvents.length > 0">
            <DynamicContentTable
              :data="specialEvents"
              :type="type"
              :write-permission="writePermission"
              :can-be-sent="true"
              @edit="editItem"
              @delete="handleDelete"
              @send="eventToSend = $event"
              @schedule="eventToSend = { ...$event, toSchedule: true }"
            />

            <Paginate
              ref="paginate"
              v-if="pageCount"
              v-model="page"
              :page-count="pageCount"
              :click-handler="changePage"
              prev-text="Précédent"
              next-text="Suivant"
              container-class="pagination"
              class="mt-1"
            ></Paginate>
          </div>

          <p v-else class="text-center">Aucun événement trouvé</p>
        </div>
      </div>
    </div>

    <ModalDynamicContent
      :show="modal.show"
      :dynamic-content="modal.dynamicContent"
      :type="type"
      :write-permission="writePermission"
      :show-user-filters="true"
      @created="itemCreated"
      @updated="itemUpdated"
      @close="closeModal"
    />

    <ModalSendDynamicContent
      v-if="hasWritePermission"
      :show="!!eventToSend"
      :dynamic-content="eventToSend"
      @sent="itemUpdated"
      @scheduled="itemUpdated"
      @close="eventToSend = null"
    />
  </div>
</template>

<script>
import Paginate from "vuejs-paginate";
import DynamicContentTable from "@/components/content/DynamicContentTable";
import DynamicContentFilters from "@/components/content/DynamicContentFilters";
import Loader from "@/components/utils/Loader";
import ModalDynamicContent from "@/components/modal/ModalDynamicContent";
import ModalSendDynamicContent from "@/components/modal/ModalSendDynamicContent";

export default {
  components: {
    DynamicContentFilters,
    Loader,
    ModalDynamicContent,
    DynamicContentTable,
    Paginate,
    ModalSendDynamicContent,
  },

  data() {
    return {
      loading: false,
      filters: {
        begin: null,
        end: null,
        name: "",
        publishedStatus: null,
      },
      type: "COACH_SPECIAL_EVENT",
      writePermission: "DYNAMIC_CONTENT_COACH_APP_SPECIAL_EVENTS_WRITE",
      specialEvents: [],
      total: 0,
      page: 1,
      offset: 0,
      limit: 15,
      count: true,
      modal: {
        show: false,
        dynamicContent: null,
      },
      eventToSend: null,
    };
  },

  computed: {
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },

    hasWritePermission() {
      return this.hasPermission(this.$store.state.user, this.writePermission);
    },
  },

  watch: {
    page() {
      if (this.page - 1 !== this.$refs.paginate.selected) {
        this.$refs.paginate.selected = this.page - 1;
      }
    },
  },

  created() {
    this.fetchSpecialEvents();
  },

  methods: {
    async fetchSpecialEvents() {
      try {
        this.loading = true;

        const { data } = await this.$api.get("/dynamic-contents", {
          params: {
            filters: { ...this.filters, type: this.type },
            offset: this.offset,
            limit: this.limit,
            currentTotal: this.total,
            count: this.count,
          },
        });

        this.specialEvents = data.data;
        this.total = data.total;
        this.count = false;
      } catch (e) {
        this.specialEvents = [];
      } finally {
        this.loading = false;
      }
    },

    applyFilters(filters) {
      this.filters = { ...this.filters, ...filters };
      this.count = true;

      this.changePage(1);
    },

    changePage(page) {
      this.page = page;
      this.offset = page * this.limit - this.limit;

      this.fetchSpecialEvents();
    },

    itemCreated(item) {
      this.specialEvents.unshift(item);
      this.total++;
    },

    itemUpdated(item) {
      this.specialEvents = this.specialEvents.map((specialEvent) => {
        if (specialEvent.id === item.id) {
          return {
            ...specialEvent,
            ...item,
            userFilters: item.userFilters
              ? {
                  ...specialEvent.userFilters,
                  ...item.userFilters,
                  emails:
                    item.userFilters.emails?.map((coach) => {
                      if (typeof coach === "string") {
                        const option = specialEvent.userFilters.emails.find(
                          (o) => o.value === coach,
                        );

                        return option || { value: coach, label: coach };
                      }

                      return coach;
                    }) || [],
                }
              : null,
          };
        }

        return specialEvent;
      });
    },

    editItem(item) {
      this.modal.dynamicContent = { ...item };
      this.modal.show = true;
    },

    closeModal() {
      this.modal.show = false;
      this.modal.dynamicContent = null;
    },

    handleDelete(item) {
      if (!item || !item.id) {
        return;
      }

      this.$confirm({
        message: `Êtes-vous sûr.e de vouloir supprimer l'événement "${item.name}" ?`,
        onConfirm: () => this.deleteItem(item.id),
      });
    },

    async deleteItem(itemId) {
      try {
        this.loading = true;

        await this.$api.delete(`/dynamic-contents/${itemId}`);

        this.specialEvents = this.specialEvents.filter(
          ({ id }) => id !== itemId,
        );
        this.total--;

        this.$successToast("Événement supprimé");
      } catch (e) {
        this.$errorToast("Impossible de supprimer cet événement");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
